import { AlertCircle, ArrowLeft, CheckCircle, Clock, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  buttonVariants,
} from '@cold-ambulance/shadcn';
import {
  ContractPageSkeleton,
  SignatureStatusCard,
  SigningEmailNotice,
  ViewGeneratedFile,
  formatDate,
  useData,
} from '@cold-ambulance/ui';

export default function OfferContractPage() {
  const { t } = useTranslation();
  const { requestId } = useParams();

  return (
    <div className="page-container">
      <div className="mx-auto w-full max-w-2xl">
        <Link
          to={`/requests/${requestId}`}
          className="text-primary mb-6 flex items-center gap-1 text-sm font-bold"
        >
          <span>{t('contract.backToRequest')}</span>
          <ArrowLeft className="h-4 w-4" />
        </Link>

        <h1 className="mb-2 text-3xl font-bold">
          {t('contract.offerDetails')}
        </h1>
        <p className="text-muted-foreground mb-8">
          {t('contract.reviewOfferAndContract')}
        </p>

        <ContractStatusBody />
      </div>
    </div>
  );
}

function ContractStatusBody() {
  const { t } = useTranslation();
  const { requestId, offerId } = useParams();

  const contractStatusQuery = useData(
    'GET /offers/{id}/contract/status',
    { id: offerId! },
    { enabled: !!offerId },
  );

  if (contractStatusQuery.isLoading) {
    return <ContractPageSkeleton />;
  }

  if (contractStatusQuery.isError) {
    const errorMessage =
      contractStatusQuery.error instanceof Error
        ? contractStatusQuery.error.message
        : t('contract.errorLoadingData');

    return (
      <>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('contract.error')}</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
        <Link
          to={`/requests/${requestId}`}
          className={buttonVariants({
            variant: 'outline',
            className: 'mt-4',
          })}
        >
          {t('contract.backToRequest')}
        </Link>
      </>
    );
  }

  const data = contractStatusQuery.data;
  if (!data) return null;

  const { vendorSigned, customerSigned, bothSigned, ttDeadline } = data;

  return (
    <>
      <FinancialOfferCard offerId={offerId!} />
      <SignatureStatusCard
        firstSigned={vendorSigned}
        secondSigned={customerSigned}
        firstLabel={t('contract.vendorSignature')}
        secondLabel={t('contract.yourSignature')}
      />
      {bothSigned && <BothSignedNotice ttDeadline={ttDeadline} />}
      {!customerSigned && <SigningEmailNotice />}
      {customerSigned && !vendorSigned && <AwaitingVendorNotice />}
    </>
  );
}

function FinancialOfferCard({ offerId }: { offerId: string }) {
  const { t } = useTranslation();

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Eye className="h-5 w-5" />
          {t('contract.financialOffer')}
        </CardTitle>
        <CardDescription>{t('contract.previewFinancialOffer')}</CardDescription>
      </CardHeader>
      <CardContent>
        <ViewGeneratedFile
          endpoint="GET /offers/{id}/pdf"
          input={{ id: offerId }}
          icon={<Eye className="h-4 w-4" />}
          variant="outline"
          className="w-full"
          label={t('contract.financialOffer')}
        />
      </CardContent>
    </Card>
  );
}

function BothSignedNotice({ ttDeadline }: { ttDeadline: string | null }) {
  const { t } = useTranslation();

  return (
    <Card className="mb-6 border-green-200 bg-green-50">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
          <div>
            <p className="font-medium text-green-800">
              {t('contract.contractSignedSuccess')}
            </p>
            <p className="mt-1 text-sm text-green-700">
              {t('contract.contractSignedBothParties')}
            </p>
            {ttDeadline && (
              <p className="mt-2 text-sm text-green-700">
                {t('contract.ttDeadline')}{' '}
                <span className="font-medium">{formatDate(ttDeadline)}</span>
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AwaitingVendorNotice() {
  const { t } = useTranslation();

  return (
    <Card className="border-amber-200 bg-amber-50">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <p className="font-medium text-amber-800">
              {t('contract.awaitingVendorSignature')}
            </p>
            <p className="mt-1 text-sm text-amber-700">
              {t('contract.awaitingVendorSignatureDescription')}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
